/* eslint-disable max-len */
import { ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import ManualInputPage from '../../../../components/ManualInputPage/ManualInputPage';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  clearExitAdjustment,
  saveExitAdjustment,
  selectExitAdjustmentDataEntryMode,
  selectExitAdjustmentExitAdjustment,
  selectExitAdjustmentFormViewModel,
  selectExitAdjustmentLoadingExitAdjustment,
  selectExitAdjustmentLoadingFormViewModel,
  selectExitAdjustmentStudentFiles,
  setExitAdjustmentDataEntryMode,
  setStudentFiles,
} from '../../../../redux/slices/exitAdjustmentSlice';
import {
  fetchExitAdjustmentById,
  fetchExitAdjustmentFormViewModel,
} from '../../../../redux/thunks/exitAdjustmentThunks';
import {
  createExitAdjustment,
  deleteExitAdjustmentFile,
  updateExitAdjustment,
} from '../../../../services/exitAdjustment.service';
import { appRoutePaths } from '../../../../services/route.service';
import { GLOBAL_DEBUG_MODE } from '../../../../testing/debugFunctions';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { ExitAdjustmentFormViewModel } from '../../../../types/ExitAdjustmentFormViewModel';
import { ExitAdjustmentModel } from '../../../../types/ExitAdjustmentModel';
import { Tabs_STUDENT } from '../../../../types/NavigationTabs';

import ExitAdjustmentLayout from 'src/components/FormLayouts/ExitAdjustment/ExitAdjustmentLayout';
import { ManualInputCrudAction } from '../../../../types/propTypes/ManualInputPagePropTypes';
import { getExitAdjustmentValidationSchema } from '../../../../types/Validation/ExitAdjustmentValidationSchema';
import { buildUserAccessNavigationTabItems } from '../../../../utilities/routeUtilities';
import './ExitAdjustmentInput.css';

const ExitAdjustmentInput = (): ReactElement => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const exitAdjustmentModel: ExitAdjustmentModel = useAppSelector(
    selectExitAdjustmentExitAdjustment
  );
  const formViewModel: ExitAdjustmentFormViewModel = useAppSelector(
    selectExitAdjustmentFormViewModel
  );
  const isLoadingFormViewModel = useAppSelector(
    selectExitAdjustmentLoadingFormViewModel
  );
  const isLoadingExitAdjustmentModel = useAppSelector(
    selectExitAdjustmentLoadingExitAdjustment
  );
  const dataEntryMode: DataEntryMode = useAppSelector(
    selectExitAdjustmentDataEntryMode
  );
  const studentFiles = useAppSelector(selectExitAdjustmentStudentFiles);
  const isLoading = isLoadingExitAdjustmentModel || isLoadingFormViewModel;

  const filters = location.state?.filters;
  const allowEdit = location.state?.allowEdit ?? true;

  const currentSearchParams = new URLSearchParams(location.search);
  const urlSchoolYear = currentSearchParams.get('schoolYear');
  const schoolYear = urlSchoolYear || filters?.schoolYear;

  const buildReturnUrl = (): string => {
    const urlLea = currentSearchParams.get('lea');
    const urlSchool = currentSearchParams.get('school');
    const urlStatus = currentSearchParams.get('status');

    if (urlSchoolYear || urlLea || urlSchool || urlStatus) {
      const searchParams = new URLSearchParams();
      if (urlSchoolYear) searchParams.set('schoolYear', urlSchoolYear);
      if (urlLea) searchParams.set('lea', urlLea);
      if (urlSchool) searchParams.set('school', urlSchool);
      if (urlStatus) searchParams.set('status', urlStatus);

      const queryString = searchParams.toString();
      return `${appRoutePaths.ExitAdjustmentList}${queryString ? `?${queryString}` : ''
        }`;
    }

    if (filters) {
      const searchParams = new URLSearchParams();
      if (filters.schoolYear)
        searchParams.set('schoolYear', filters.schoolYear);
      if (filters.lea) searchParams.set('lea', filters.lea);
      if (filters.school) searchParams.set('school', filters.school);
      if (filters.status) searchParams.set('status', filters.status);

      const queryString = searchParams.toString();
      return `${appRoutePaths.ExitAdjustmentList}${queryString ? `?${queryString}` : ''
        }`;
    }
    return appRoutePaths.ExitAdjustmentList;
  };

  const buildCreateSuccessUrl = (id: string): string => {
    const urlLea = currentSearchParams.get('lea');
    const urlSchool = currentSearchParams.get('school');
    const urlStatus = currentSearchParams.get('status');

    if (urlSchoolYear || urlLea || urlSchool || urlStatus) {
      const searchParams = new URLSearchParams();
      if (urlSchoolYear) searchParams.set('schoolYear', urlSchoolYear);
      if (urlLea) searchParams.set('lea', urlLea);
      if (urlSchool) searchParams.set('school', urlSchool);
      if (urlStatus) searchParams.set('status', urlStatus);

      const queryString = searchParams.toString();
      return `${appRoutePaths.ExitAdjustmentInput(id)}${queryString ? `?${queryString}` : ''
        }`;
    }

    if (filters) {
      const searchParams = new URLSearchParams();
      if (filters.schoolYear)
        searchParams.set('schoolYear', filters.schoolYear);
      if (filters.lea) searchParams.set('lea', filters.lea);
      if (filters.school) searchParams.set('school', filters.school);
      if (filters.status) searchParams.set('status', filters.status);

      const queryString = searchParams.toString();
      return `${appRoutePaths.ExitAdjustmentInput(id)}${queryString ? `?${queryString}` : ''
        }`;
    }
    return appRoutePaths.ExitAdjustmentInput(id);
  };

  const supportedNonGenericErrors = [
    {
      statusCode: 400,
    },
  ];

  // Documents are uploaded against this id before the request exists, so creation must reuse it.
  const handleCreate = (
    model: ExitAdjustmentModel
  ): ReturnType<typeof createExitAdjustment> =>
    createExitAdjustment({
      ...model,
      id: model.id || formViewModel.newExitAdjustmentId,
    });

  const handleCancel = async (): Promise<void> => {
    if (dataEntryMode !== DataEntryMode.NEW || studentFiles.length === 0) {
      return;
    }

    await Promise.all(
      studentFiles.map((file) =>
        deleteExitAdjustmentFile(file.leaIdentifier, file.id)
      )
    );
    dispatch(setStudentFiles([]));
  };

  return (
    <>
      <ManualInputPage<ExitAdjustmentModel, ExitAdjustmentFormViewModel>
        actions={ManualInputCrudAction.CREATE_AND_EDIT}
        loadingOptions={{
          isLoading: isLoading,
          loadingDataId: 'exit-adjustment-input-loader',
        }}
        navigationTabs={buildUserAccessNavigationTabItems(Tabs_STUDENT)}
        classes={{ pageClass: 'data-input--exit-adjustment--input' }}
        parameterOptions={{
          routeParameterName: 'exitAdjustmentId',
          identityName: exitAdjustmentModel.id,
        }}
        routes={{
          backBarRoute: buildReturnUrl(),
          cancelRoute: buildReturnUrl(),
          createSuccessRoute: buildCreateSuccessUrl,
        }}
        dataFunctions={{
          loadDataDetailById: fetchExitAdjustmentById,
          loadFormDetail: fetchExitAdjustmentFormViewModel,
          setEntryMode: setExitAdjustmentDataEntryMode,
          setData: saveExitAdjustment,
          clearData: clearExitAdjustment,
          onCreate: handleCreate,
          onUpdate: updateExitAdjustment,
          onCancel: handleCancel,
        }}
        formDetails={{
          validationSchema: getExitAdjustmentValidationSchema(dataEntryMode),
          initialFormValues: exitAdjustmentModel,
          isFormModelEmpty:
            dataEntryMode === DataEntryMode.NEW ||
            !formViewModel.defaultSchoolYear,
        }}
        config={{
          dataEntryMode: dataEntryMode,
          objectVerbiage: 'Exit Adjustment',
          pageHeader: 'Student',
          supportedNonGenericErrors: supportedNonGenericErrors,
        }}
        DEV_MODE={GLOBAL_DEBUG_MODE}
      >
        <ExitAdjustmentLayout schoolYear={schoolYear} allowEdit={allowEdit} />
      </ManualInputPage>
    </>
  );
};

export default ExitAdjustmentInput;
